import { Injectable } from '@angular/core'
import {
  NgModalServiceInterface,
  ModalResponse,
} from '../../models/modal.model'
import { NgNotificationServiceInterface } from '../../models/notification.model'
import { Activity } from './models/activity.entity'
import {
  combineLatest,
  from,
  map,
  Observable,
  of,
  switchMap,
  concatMap,
  scan,
  last,
} from 'rxjs'
import { Page } from '../../models/util.model'
import { uniqBy } from 'lodash'
import { ActivitiesService } from './activities.service'
import { Execution, ExecutionsService } from '../executions'
import {
  ActivitiesListingData,
  ActivitiesListingPage,
} from './models/activity-listing.model'
import {
  ActivityField,
  ActivitySearchParams,
} from './models/activity-search.model'
import { parseActivityPageKeys } from './libs/activity-page.lib'
import {
  ActivityModalCode,
  ActivityNotificationCode,
  parseActivityModal,
  parseActivityNotification,
} from './libs/activity-notification.lib'

@Injectable({
  providedIn: 'root',
})
export class ActivitiesRepository {
  constructor(
    private activitiesService: ActivitiesService,
    private executionsService: ExecutionsService,
    private ngModalService?: NgModalServiceInterface,
    private ngNotificationService?: NgNotificationServiceInterface,
  ) {}

  /**
   * Search goods-returns and external data
   * @param searchParams - the search params
   * @param fields - the fields
   * @param listingData - the already loaded listing data
   * @returns the observable for search goods-returns
   */
  searchActivities$(
    searchParams: ActivitySearchParams,
    fields: ActivityField[],
    listingData?: ActivitiesListingData,
    returnAll?: boolean,
  ): Observable<ActivitiesListingPage> {
    const needsPagination =
      searchParams.limit !== undefined &&
      searchParams.limit > this.activitiesService.limit

    return needsPagination
      ? this._paginateActivities$(searchParams, fields, listingData)
      : this._searchActivities$(searchParams, fields, listingData, returnAll)
  }

  /**
   * Search goods returns with relative external data
   * @param searchParams - the search params
   * @param fields - the goods-returns fields
   * @param listingData - the already loaded listing data
   * @returns the observable<ActivitiesListingPage> for search goods-returns
   */
  private _searchActivities$(
    searchParams: ActivitySearchParams,
    fields: ActivityField[],
    listingData?: ActivitiesListingData,
    returnAll?: boolean,
  ): Observable<ActivitiesListingPage> {
    return this.activitiesService
      .search$(searchParams, returnAll)
      .pipe(
        switchMap((page) =>
          this._loadExtData$(page, fields, listingData).pipe(
            map((extData) => ({ ...page, extData })),
          ),
        ),
      )
  }

  /**
   * Load external page goods-returns data
   * @param page - the goods-returns page
   * @param fields - the goods-returns fields
   * @returns the observable for load external data
   */
  private _loadExtData$(
    page: Page<Activity>,
    fields: ActivityField[],
    listingData?: ActivitiesListingData,
  ): Observable<ActivitiesListingData> {
    const extDataKeys = parseActivityPageKeys(page)
    const obs$: Partial<{
      executions: Observable<Execution[]>
    }> = {}

    if (fields.includes('lastExecutionId') && extDataKeys.executionIds) {
      const existingActivities = listingData?.executions || []
      const hasAllActivities = extDataKeys.executionIds.every((id) =>
        existingActivities.some((channel) => channel._id === id),
      )

      if (!hasAllActivities) {
        obs$.executions = this.executionsService.list$({
          _id: extDataKeys.executionIds,
        })
      } else {
        obs$.executions = of(existingActivities)
      }
    }

    if (!Object.keys(obs$).length) {
      return of({} as ActivitiesListingData)
    }

    return combineLatest(obs$).pipe(
      map((results) => {
        const extData: ActivitiesListingData = {}
        if (results.executions) extData.executions = results.executions
        return extData
      }),
    )
  }

  /**
   * Search more than 200 goods-returns with relative external data
   * @param searchParams - the search params
   * @param fields - the goods-returns fields
   * @param listingData - the already loaded listing data
   * @returns the observable<ActivitiesListingPage> for search goods-returns
   */
  private _paginateActivities$(
    searchParams: ActivitySearchParams,
    fields: ActivityField[],
    listingData?: ActivitiesListingData,
  ): Observable<ActivitiesListingPage> {
    const totalCount = searchParams.limit || this.activitiesService.limit
    const pagesCount = Math.ceil(totalCount / this.activitiesService.limit)

    return from(
      Array.from({ length: pagesCount }, (_, i) => ({
        ...searchParams,
        limit: this.activitiesService.limit,
        offset: i * this.activitiesService.limit,
      })),
    ).pipe(
      concatMap((pageParams) =>
        this._searchActivities$(pageParams, fields, listingData),
      ),
      scan<ActivitiesListingPage, ActivitiesListingPage>(
        (acc, current) => ({
          totalCount: uniqBy(
            [...(acc.data || []), ...(current.data || [])],
            '_id',
          ).length,
          data: uniqBy([...(acc.data || []), ...(current.data || [])], '_id'),
          extData: { ...acc.extData, ...current.extData },
        }),
        { totalCount: 0, data: [], extData: {} } as ActivitiesListingPage,
      ),
      last(),
    )
  }

  notify(options: { code?: ActivityNotificationCode; message?: string }): void {
    this.ngNotificationService?.show(
      options.message
        ? { title: 'Error', message: options.message, color: 'error' }
        : parseActivityNotification(options.code),
    )
  }

  async alert(
    code: ActivityModalCode,
    activity?: Activity,
  ): Promise<ModalResponse> {
    const settings = parseActivityModal(code, activity)
    if (!this.ngModalService || !settings) {
      return 'closed'
    }
    return await this.ngModalService.showDialog(settings)
  }
}
